"use client"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import { submitCommand } from "../lib/api-client.ts"
import type { UserView } from "../lib/user-view.ts"
import { useUnreadNotificationsPublisher } from "./app-shell.tsx"
import styles from "./my-support.module.css"
import { NotificationDetailSheet, type NotificationEntry, notificationDestinationHref } from "./notification-detail-sheet.tsx"
import { PaginationControls } from "./pagination-controls.tsx"
import { RedesignAccountEmpty } from "./redesign-account-empty.tsx"

type NotificationsViewModel = Extract<
  UserView,
  {
    readonly kind: "notifications"
  }
>
const dateTimeFormatter = new Intl.DateTimeFormat("ko-KR", { dateStyle: "medium", timeStyle: "short" })
const notificationPageSize = 10
export const NotificationsView = ({ view }: { readonly view: NotificationsViewModel }) => {
  const router = useRouter()
  const publishUnread = useUnreadNotificationsPublisher()
  const [readIds, setReadIds] = useState<ReadonlySet<string>>(new Set())
  const [message, setMessage] = useState("")
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedNotification, setSelectedNotification] = useState<NotificationEntry | null>(null)
  const notifications = view.notifications.map((notification) => (readIds.has(notification.id) ? { ...notification, read: true } : notification))
  const hasUnread = notifications.some((notification) => !notification.read)
  const totalPages = Math.max(1, Math.ceil(notifications.length / notificationPageSize))
  const pageNotifications = notifications.slice((currentPage - 1) * notificationPageSize, currentPage * notificationPageSize)
  useEffect(() => {
    publishUnread(hasUnread)
  }, [hasUnread, publishUnread])
  const markRead = async (id: string): Promise<void> => {
    setPendingId(id)
    setMessage("")
    try {
      await submitCommand(`/web/notifications/${id}/read`, { notificationId: id })
      setReadIds((current) => new Set([...current, id]))
    } catch (error: unknown) {
      if (error instanceof Error) setMessage("알림 상태를 저장하지 못했습니다. 다시 시도해 주세요.")
      else throw error
    } finally {
      setPendingId(null)
    }
  }
  const openNotification = (notification: NotificationEntry): void => {
    if (!notification.read && !readIds.has(notification.id)) void markRead(notification.id)
    const href = notificationDestinationHref(notification)
    if (notification.destination === "support" && href !== null) {
      router.push(href)
      return
    }
    setSelectedNotification(notification)
  }
  return (
    <div className={`${styles["notificationLayout"]}${" redesign-notification-layout"}`} data-notification-layout>
      {
        <header className="redesign-notification-intro">
          <h2>알림</h2>
          <p>전환과 상품 처리 소식을 확인하세요.</p>
        </header>
      }
      {notifications.length === 0 ? (
        <RedesignAccountEmpty kind="notifications" />
      ) : (
        <ul className={styles["notificationList"]} data-notification-list>
          {pageNotifications.map((notification) => (
            <li className={`${styles["notificationItem"]} ${notification.read ? styles["read"] : styles["unread"]}`} data-notification-item data-read-state={notification.read ? "read" : "unread"} key={notification.id}>
              <button aria-label={`${notification.title} 상세 보기`} className={styles["notificationAction"]} disabled={pendingId === notification.id} onClick={() => openNotification(notification)} type="button">
                <span className={styles["notificationCopy"]}>
                  <span className={styles["notificationHeading"]}>
                    <strong>{notification.title}</strong>
                    {notification.read ? null : <span aria-label="읽지 않음" className={styles["notificationUnreadIndicator"]} role="img" />}
                  </span>
                  <p>{notification.body}</p>
                  <time dateTime={notification.occurredAt}>{dateTimeFormatter.format(new Date(notification.occurredAt))}</time>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <PaginationControls currentPage={currentPage} label="알림 목록 페이지" onPageChange={setCurrentPage} totalPages={totalPages} />
      {message.length === 0 ? null : (
        <p aria-live="assertive" className={styles["notificationError"]} data-notification-error>
          {message}
        </p>
      )}
      <NotificationDetailSheet notification={selectedNotification} onClose={() => setSelectedNotification(null)} />
    </div>
  )
}
